import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { CardBox, Badge, Table, ScoreBadge } from './UI';

const AggregatedDashboard = ({ executions, searchSources = [], viewMode = 'simple' }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const [disabledCategories, setDisabledCategories] = useState(new Set());

  // Data aggregation functions
  const aggregatedData = useMemo(() => {
    if (!executions || executions.length === 0) {
      return {
        averageClientScore: 0,
        clientVisibilityRate: 0,
        totalCompetitors: 0,
        topCompetitors: [],
        topTrackedCompetitors: [],
        topFoundCompetitors: [],
        engineStats: {},
        engineVisibilityScores: {},
        bestQueries: [],
        totalSuccessful: 0,
        totalFailed: 0,
        topSources: [],
        totalUniqueSources: 0,
        totalCitations: 0
      };
    }

    const successful = executions.filter(exec => exec.status === 'completed');
    const failed = executions.filter(exec => exec.status === 'failed');
    const totalExecutions = successful.length;

    // Calculate average client score (including 0 scores for accurate average)
    const scores = successful
      .map(exec => exec.ai_analysis_results?.summary?.overallClientScore || 0);
    const averageClientScore = scores.length > 0 ?
      Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length) : 0;

    // Calculate client visibility rate
    const clientMentions = successful.filter(exec =>
      exec.ai_analysis_results?.summary?.clientMentions > 0
    ).length;
    const clientVisibilityRate = successful.length > 0 ?
      Math.round((clientMentions / successful.length) * 100) : 0;

    // Aggregate competitors
    const competitorMap = new Map();
    const trackedCompetitorMap = new Map();
    const foundCompetitorMap = new Map();

    successful.forEach(exec => {
      const competitors = exec.ai_analysis_results?.summary?.topCompetitors || [];
      const trackedCompetitors = exec.ai_analysis_results?.summary?.trackedCompetitors || [];
      const foundCompetitors = exec.ai_analysis_results?.summary?.foundCompetitors || [];

      // Process all competitors
      competitors.forEach(comp => {
        if (competitorMap.has(comp.name)) {
          const existing = competitorMap.get(comp.name);
          existing.mentions += comp.mentions || 0;
          existing.queries.add(exec.searchTerm);
          existing.totalScore += comp.averageScore || 0;
          existing.appearances++;
          existing.isTracked = existing.isTracked || comp.isTracked;
        } else {
          competitorMap.set(comp.name, {
            name: comp.name,
            mentions: comp.mentions || 0,
            queries: new Set([exec.searchTerm]),
            totalScore: comp.averageScore || 0,
            appearances: 1,
            isTracked: comp.isTracked || false
          });
        }
      });

      // Process tracked competitors
      trackedCompetitors.forEach(comp => {
        if (trackedCompetitorMap.has(comp.name)) {
          const existing = trackedCompetitorMap.get(comp.name);
          existing.mentions += comp.mentions || 0;
          existing.queries.add(exec.searchTerm);
          existing.totalScore += comp.averageScore || 0;
          existing.appearances++;
        } else {
          trackedCompetitorMap.set(comp.name, {
            name: comp.name,
            mentions: comp.mentions || 0,
            queries: new Set([exec.searchTerm]),
            totalScore: comp.averageScore || 0,
            appearances: 1,
            isTracked: true
          });
        }
      });

      // Process found competitors
      foundCompetitors.forEach(comp => {
        if (foundCompetitorMap.has(comp.name)) {
          const existing = foundCompetitorMap.get(comp.name);
          existing.mentions += comp.mentions || 0;
          existing.queries.add(exec.searchTerm);
          existing.totalScore += comp.averageScore || 0;
          existing.appearances++;
        } else {
          foundCompetitorMap.set(comp.name, {
            name: comp.name,
            mentions: comp.mentions || 0,
            queries: new Set([exec.searchTerm]),
            totalScore: comp.averageScore || 0,
            appearances: 1,
            isTracked: false
          });
        }
      });
    });

    // Convert to sorted arrays
    const processCompetitorMap = (map) => Array.from(map.values())
      .map(comp => ({
        ...comp,
        queries: comp.queries.size,
        averageScore: Math.round(comp.totalScore / comp.appearances),
        visibilityRate: totalExecutions > 0 ? Math.round((comp.appearances / totalExecutions) * 100) : 0
      }))
      .sort((a, b) => b.visibilityRate - a.visibilityRate || b.mentions - a.mentions || b.queries - a.queries);

    const topCompetitors = processCompetitorMap(competitorMap).slice(0, 8);
    const topTrackedCompetitors = processCompetitorMap(trackedCompetitorMap).slice(0, 5);
    const topFoundCompetitors = processCompetitorMap(foundCompetitorMap).slice(0, 5);

    // Engine statistics
    const engineStats = {};
    const engineVisibilityScores = {};

    successful.forEach(exec => {
      if (exec.results?.engines) {
        Object.entries(exec.results.engines).forEach(([engine, result]) => {
          if (!engineStats[engine]) {
            engineStats[engine] = { success: 0, total: 0 };
            engineVisibilityScores[engine] = { totalScore: 0, count: 0, clientMentions: 0 };
          }
          engineStats[engine].total++;
          if (result.status === 'success') {
            engineStats[engine].success++;
          }
        });
      }

      // Process engine-specific visibility from AI analysis results
      if (exec.ai_analysis_results?.engines) {
        // We have per-engine analysis data!
        Object.entries(exec.ai_analysis_results.engines).forEach(([engine, engineAnalysis]) => {
          if (!engineVisibilityScores[engine]) {
            engineVisibilityScores[engine] = { totalScore: 0, count: 0, clientMentions: 0 };
          }

          // Only count successful engine analyses
          if (engineAnalysis.status !== 'failed') {
            engineVisibilityScores[engine].count++;

            // Check for actual client mentions in this specific engine
            if (engineAnalysis.clientAnalysis?.mentions > 0) {
              engineVisibilityScores[engine].clientMentions++;
            }

            // Add the engine-specific score
            const score = engineAnalysis.clientAnalysis?.score || 0;
            engineVisibilityScores[engine].totalScore += score;
          }
        });
      } else if (exec.results?.engines && exec.ai_analysis_results?.summary) {
        // Fallback: distribute visibility if we don't have per-engine analysis
        const summary = exec.ai_analysis_results.summary;
        const clientMentioned = summary.clientMentions > 0;

        Object.entries(exec.results.engines).forEach(([engine, result]) => {
          if (!engineVisibilityScores[engine]) {
            engineVisibilityScores[engine] = { totalScore: 0, count: 0, clientMentions: 0 };
          }

          if (result.status === 'success') {
            engineVisibilityScores[engine].count++;

            if (clientMentioned && result.textAnswer) {
              engineVisibilityScores[engine].clientMentions++;
              engineVisibilityScores[engine].totalScore += summary.overallClientScore || 0;
            }
          }
        });
      }
    });

    // Calculate visibility scores
    Object.keys(engineVisibilityScores).forEach(engine => {
      const stats = engineVisibilityScores[engine];
      stats.visibilityRate = stats.count > 0 ?
        Math.round((stats.clientMentions / stats.count) * 100) : 0;
      stats.averageScore = stats.count > 0 ?
        Math.round(stats.totalScore / stats.count) : 0;
    });

    // Calculate overall visibility as AVERAGE of per-engine visibility rates
    const engineVisibilityRates = Object.values(engineVisibilityScores).map(stats => stats.visibilityRate || 0);
    const overallClientVisibilityRate = engineVisibilityRates.length > 0 ?
      Math.round(engineVisibilityRates.reduce((sum, rate) => sum + rate, 0) / engineVisibilityRates.length) : clientVisibilityRate;

    // Best performing queries
    const bestQueries = successful
      .filter(exec => exec.ai_analysis_results?.summary?.overallClientScore > 0)
      .map(exec => ({
        query: exec.searchTerm,
        score: exec.ai_analysis_results.summary.overallClientScore,
        clientMentions: exec.ai_analysis_results.summary.clientMentions || 0
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);

    // Source aggregation
    const sourceMap = new Map();
    const uniqueDomains = new Set();
    const categoryCountMap = new Map();

    searchSources.forEach(source => {
      if (source.domain) {
        uniqueDomains.add(source.domain);

        // Track category counts separately
        const category = source.category || 'Unknown';
        if (categoryCountMap.has(category)) {
          categoryCountMap.get(category).count++;
          categoryCountMap.get(category).domains.add(source.domain);
        } else {
          categoryCountMap.set(category, {
            name: category,
            count: 1,
            domains: new Set([source.domain])
          });
        }

        if (sourceMap.has(source.domain)) {
          const existing = sourceMap.get(source.domain);
          existing.count++;
          existing.engines.add(source.engine);
          existing.categories.add(source.category || 'Unknown');
        } else {
          sourceMap.set(source.domain, {
            domain: source.domain,
            count: 1,
            engines: new Set([source.engine]),
            categories: new Set([source.category || 'Unknown']),
            category: source.category || 'Unknown'
          });
        }
      }
    });

    // Convert to sorted array
    const topSources = Array.from(sourceMap.values())
      .map(source => ({
        ...source,
        engines: Array.from(source.engines)
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return {
      averageClientScore,
      clientVisibilityRate: overallClientVisibilityRate,
      totalCompetitors: topCompetitors.length,
      topCompetitors,
      topTrackedCompetitors,
      topFoundCompetitors,
      engineStats,
      engineVisibilityScores,
      bestQueries,
      totalSuccessful: successful.length,
      totalFailed: failed.length,
      topSources,
      totalUniqueSources: uniqueDomains.size,
      totalCitations: searchSources.length
    };
  }, [executions, searchSources]);

  const getEngineIcon = (engine) => {
    const engineName = engine.toLowerCase();
    const logoMap = {
      'openai': '/logos/chatgpt.png',
      'chatgpt': '/logos/chatgpt.png',
      'gemini': '/logos/gemini.png',
      'perplexity': '/logos/perplexity.png'
    };

    const logoSrc = logoMap[engineName];
    if (logoSrc) {
      return (
        <img
          src={logoSrc}
          alt={`${engine} logo`}
          className="w-8 h-8 rounded-full object-cover"
        />
      );
    }
    return <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700"></div>;
  };

  // Prepare data for charts
  const engineChartData = useMemo(() => {
    return Object.entries(aggregatedData.engineVisibilityScores)
      .map(([engine, stats]) => ({
        engine: engine === 'openai' ? 'ChatGPT' : engine.charAt(0).toUpperCase() + engine.slice(1),
        visibility: stats.visibilityRate || 0
      }));
  }, [aggregatedData.engineVisibilityScores]);

  // Category translations mapping
  const getCategoryTranslation = (category) => {
    const translationKey = `dashboard.categories.${category}`;
    const translated = t(translationKey);
    // If translation doesn't exist, return original
    return translated === translationKey ? category : translated;
  };

  // Group sources by category
  const sourceCategories = useMemo(() => {
    // Use searchSources directly to count categories properly
    const categoryMap = new Map();

    searchSources.forEach(source => {
      const category = source.category || 'Unknown';
      // Skip Unknown category
      if (category === 'Unknown') return;

      if (categoryMap.has(category)) {
        categoryMap.get(category).count++;
      } else {
        categoryMap.set(category, {
          name: category,
          count: 1
        });
      }
    });

    return Array.from(categoryMap.values())
      .map(cat => ({
        name: cat.name,
        displayName: getCategoryTranslation(cat.name),
        value: cat.count
      }))
      .sort((a, b) => b.value - a.value);
  }, [searchSources, t]);

  // Filter chart data based on disabled categories
  const filteredChartData = useMemo(() => {
    return sourceCategories.filter(item => !disabledCategories.has(item.name));
  }, [sourceCategories, disabledCategories]);

  // Handle category click to toggle enable/disable
  const handleCategoryClick = (data) => {
    // Handle both direct data (from pie click) and legend click
    let categoryName = data?.name || data;

    // Find the original category name from displayName if needed
    const category = sourceCategories.find(item =>
      item.displayName === categoryName || item.name === categoryName
    );

    if (category) {
      setDisabledCategories(prev => {
        const newSet = new Set(prev);
        if (newSet.has(category.name)) {
          newSet.delete(category.name);
        } else {
          newSet.add(category.name);
        }
        return newSet;
      });
    }
  };

  // Lime/olive/warm-gray brand palette (Pass 7). Mirrors GlobalDashboard.
  const COLORS = {
    'Competitor': '#9ec77f',     // Olive — primary, most common
    'Ranking': '#d8f9b8',        // Brand lime
    'Comparison': '#5fa05c',     // Deeper green
    'UserReviews': '#374151',    // Warm dark gray
    'BlogPost': '#9ca3af',       // Warm mid gray
    'News': '#6b7280',           // Warm darker gray
    'Announcements': '#3b82f6',  // Blue (overflow)
    'Podcasts': '#10b981',       // Green (overflow)
    'SocialMedia': '#f59e0b',    // Amber (overflow)
    'Wikipedia': '#ef4444',      // Red (overflow)
    'Reddit': '#8b5cf6',         // Purple (overflow)
    'Unknown': '#cbd5e1'         // Light gray
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <CardBox>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {t('dashboard.aggregated.title') || 'Batch Analysis Dashboard'}
        </h2>
      </CardBox>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 metric-grid-enter">
        <CardBox className="metric-card-accent">
          <div className="text-gray-600 dark:text-gray-400 dark:text-gray-500 text-sm font-medium mb-2">
            {t('dashboard.aggregated.visibility') || 'Visibility'}
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {aggregatedData.clientVisibilityRate}%
          </div>
          {viewMode === 'detailed' && (
            <div className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500 mt-1">
              {t('dashboard.aggregated.queriesMentioned') || 'of queries mentioned'}
            </div>
          )}
        </CardBox>

        <CardBox className="metric-card-accent">
          <div className="text-gray-600 dark:text-gray-400 dark:text-gray-500 text-sm font-medium mb-2">
            {t('dashboard.aggregated.competitors') || 'Competitors'}
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {aggregatedData.totalCompetitors}
          </div>
          {viewMode === 'detailed' && (
            <div className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500 mt-1">
              {t('dashboard.aggregated.uniqueCompanies') || 'unique companies'}
            </div>
          )}
        </CardBox>

        <CardBox className="metric-card-accent">
          <div className="text-gray-600 dark:text-gray-400 dark:text-gray-500 text-sm font-medium mb-2">
            {t('dashboard.aggregated.sources') || 'Sources'}
          </div>
          <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {aggregatedData.totalUniqueSources}
          </div>
          {viewMode === 'detailed' && (
            <div className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500 mt-1">
              {t('dashboard.aggregated.uniqueDomains') || 'unique domains'}
            </div>
          )}
        </CardBox>
      </div>

      {/* Engine Performance */}
      {Object.keys(aggregatedData.engineVisibilityScores).length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">
            {t('dashboard.aggregated.enginePerformance') || 'Engine Performance'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {Object.entries(aggregatedData.engineVisibilityScores).map(([engine, stats]) => (
              <CardBox key={engine}>
                <div className="flex items-center gap-3 mb-4">
                  {getEngineIcon(engine)}
                  <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {engine === 'openai' ? 'ChatGPT' : engine.charAt(0).toUpperCase() + engine.slice(1)}
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">Visibility</span>
                    <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {stats.visibilityRate}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        stats.visibilityRate >= 50 ? 'bg-green-500' :
                        stats.visibilityRate >= 25 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${stats.visibilityRate}%` }}
                    />
                  </div>
                </div>
              </CardBox>
            ))}
          </div>
        </div>
      )}

      {/* Charts Grid */}
      {engineChartData.length > 0 && (
        <div className="grid md:grid-cols-2 gap-8">
          {/* Engine Visibility Chart */}
          <CardBox title={t('dashboard.aggregated.visibilityByEngine') || 'Visibility by Engine'}>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={engineChartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <defs>
                    <linearGradient id="colorEngineBar" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.9}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.7}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                  <XAxis
                    dataKey="engine"
                    tick={{ fontSize: 12, fill: '#6b7280', fontWeight: 500 }}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={{ stroke: '#e5e7eb' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#6b7280', fontWeight: 500 }}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={{ stroke: '#e5e7eb' }}
                    domain={[0, 100]}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: '#ffffff',
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '12px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Bar dataKey="visibility" fill="url(#colorEngineBar)" radius={[12, 12, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardBox>

          {/* Source Categories Chart */}
          {sourceCategories.length > 0 && (
            <CardBox
              title={t('dashboard.aggregated.sourceCategories') || 'Source Categories'}
              subtitle={t('dashboard.overview.metrics.clickToToggle')}
            >
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={filteredChartData.length > 0 ? filteredChartData : sourceCategories}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''}
                      outerRadius={90}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="displayName"
                      onClick={handleCategoryClick}
                      style={{ cursor: 'pointer' }}
                      paddingAngle={2}
                    >
                      {(filteredChartData.length > 0 ? filteredChartData : sourceCategories).map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={disabledCategories.has(entry.name) ? '#e5e7eb' : (COLORS[entry.name] || '#6b7280')}
                          style={{ opacity: disabledCategories.has(entry.name) ? 0.3 : 1 }}
                        />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '12px',
                      }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      onClick={handleCategoryClick}
                      wrapperStyle={{ cursor: 'pointer' }}
                      formatter={(value) => {
                        // Find the original name for this displayName
                        const category = sourceCategories.find(item => item.displayName === value);
                        const isDisabled = category && disabledCategories.has(category.name);

                        return (
                          <span style={{
                            textDecoration: isDisabled ? 'line-through' : 'none',
                            opacity: isDisabled ? 0.5 : 1
                          }}>
                            {value}
                          </span>
                        );
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardBox>
          )}
        </div>
      )}

      {/* Top Competitors */}
      {aggregatedData.topCompetitors.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6">
            {t('dashboard.aggregated.topCompetitors') || 'Top Competitors'}
          </h3>
          <div className="space-y-3">
            {aggregatedData.topCompetitors.slice(0, 5).map((competitor, idx) => (
              <CardBox key={competitor.name} hover>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-2xl font-bold text-gray-400 dark:text-gray-500">
                      {idx + 1}
                    </span>
                    <div>
                      <div className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {competitor.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500 mt-1">
                        {competitor.queries} queries • {competitor.mentions} mentions
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {competitor.visibilityRate}%
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500">visibility</div>
                  </div>
                </div>
              </CardBox>
            ))}
          </div>
        </div>
      )}

      {/* Best Performing Queries */}
      {aggregatedData.bestQueries.length > 0 && (
        <Table
          title={t('dashboard.aggregated.bestQueries') || 'Best Performing Queries'}
          columns={[
            {
              key: 'query',
              label: t('dashboard.aggregated.queryHeader') || 'Query',
              align: isRTL ? 'right' : 'left'
            },
            {
              key: 'score',
              label: t('dashboard.aggregated.scoreHeader') || 'Score',
              align: 'center',
              render: (value) => <ScoreBadge score={value} />
            },
            {
              key: 'clientMentions',
              label: t('dashboard.aggregated.mentionsHeader') || 'Mentions',
              align: 'center'
            }
          ]}
          data={aggregatedData.bestQueries}
          hoverable
        />
      )}
    </div>
  );
};

export default AggregatedDashboard;